import { useEffect, useRef, useState } from "react";

const REFRESH_INTERVAL_MS = 60000;

export function useAutoScrollTabs({ activeTab, setActiveTab, loadTabData, loadedTabs }: {
    activeTab: string;
    setActiveTab: (tab: string) => void;
    loadTabData: (tab: string) => void;
    loadedTabs: Record<string, boolean>;
}) {
    const [autoScroll, setAutoScroll] = useState(false);
    const [showTabSelector, setShowTabSelector] = useState(false);
    const [scrollTabs, setScrollTabs] = useState<string[]>([]);

    const activeTabRef = useRef<string>(activeTab);
    const intervalRef = useRef<number>(undefined);
    const refreshTimers = useRef<{ [key: string]: number }>({});
    const needsRefresh = useRef<{ [key: string]: boolean }>({});

    useEffect(() => {
        activeTabRef.current = activeTab;

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab]);

    useEffect(() => {
        if (!autoScroll || scrollTabs.length === 0) return;

        const scrollStep = 1;
        const scrollInterval = 50;

        const scrollAndRotate = () => {
            const atBottom = window.innerHeight + window.scrollY >= document.body.scrollHeight - 2;

            if (atBottom) {
                clearInterval(intervalRef.current);
                window.scrollTo(0, 0);

                const currentIndex = scrollTabs.indexOf(activeTabRef.current);
                const nextIndex = (currentIndex + 1) % scrollTabs.length;
                const nextTab = scrollTabs[nextIndex];

                setActiveTab(nextTab);
                activeTabRef.current = nextTab;

                if (!loadedTabs[nextTab] || needsRefresh.current[nextTab]) {
                    loadTabData(nextTab);
                } else {
                    startTabTimer(nextTab);
                }

                setTimeout(() => {
                    intervalRef.current = window.setInterval(scrollAndRotate, scrollInterval);
                }, 2000);
            } else {
                window.scrollBy({ top: scrollStep, left: 0, behavior: "smooth" });
            }
        };

        intervalRef.current = window.setInterval(scrollAndRotate, scrollInterval);
        return () => clearInterval(intervalRef.current);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [autoScroll, scrollTabs]);

    function startTabTimer(tabKey: string) {
        clearInterval(refreshTimers.current[tabKey]);

        if (needsRefresh.current[tabKey]) return;

        refreshTimers.current[tabKey] = window.setInterval(() => {
            const isActive = activeTabRef.current === tabKey;

            if (isActive) {
                loadTabData(tabKey);
            } else {
                needsRefresh.current[tabKey] = true;
                clearInterval(refreshTimers.current[tabKey]);
            }
        }, REFRESH_INTERVAL_MS);
    }

    function clearAllTimers() {
        Object.values(refreshTimers.current).forEach(clearInterval);
    }

    return {
        autoScroll,
        setAutoScroll,
        showTabSelector,
        setShowTabSelector,
        scrollTabs,
        setScrollTabs,
        clearAllTimers,
    };
}
